// ============================================================
// RawEventsTab — most recent raw events from v2 `/export` (NDJSON,
// newest first), refreshed while the tab is open.
// ============================================================

import { useMemo, useState } from 'react'
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { Stream } from '@mui/icons-material'

import { useGetRecentEventsQuery } from '@/apis/v2Api'
import { useAppSelector } from '@/app/hooks'
import { useProjectId, useProjectToday } from '@/hooks/useReportArgs'
import { ReportStatus, fmtNum } from '@/modules/reports/common'

const LIMIT = 200

function formatTime(iso: string): string {
    const d = new Date(iso)
    return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function RawEventsTab() {
    const projectId = useProjectId()
    const subTenant = useAppSelector(s => s.project.subTenant)
    const [eventFilter, setEventFilter] = useState<string | null>(null)
    const today = useProjectToday()
    const {
        data = [],
        isLoading,
        error,
    } = useGetRecentEventsQuery(
        { projectId, subTenant, from: today, to: today, limit: LIMIT },
        { pollingInterval: 15_000, skipPollingIfUnfocused: true },
    )

    const topEvents = useMemo(() => {
        const counts = new Map<string, number>()
        for (const e of data) {
            counts.set(e.event, (counts.get(e.event) ?? 0) + 1)
        }
        return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12)
    }, [data])
    const rows = eventFilter ? data.filter(e => e.event === eventFilter) : data

    return (
        <ReportStatus
            isLoading={isLoading}
            error={error}
            isEmpty={data.length === 0}
            emptyIcon={<Stream />}
            emptyTitle="No events today yet"
            emptyDescription="The latest raw events appear here, refreshed every 15 seconds."
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Latest {data.length} events today
                </Typography>
                {topEvents.map(([name, count]) => (
                    <Chip
                        key={name}
                        size="small"
                        label={`${name} (${count})`}
                        color={eventFilter === name ? 'primary' : 'default'}
                        variant={eventFilter === name ? 'filled' : 'outlined'}
                        onClick={() => setEventFilter(eventFilter === name ? null : name)}
                    />
                ))}
            </Box>
            <Paper variant="outlined" sx={{ maxHeight: 560, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Time</TableCell>
                            <TableCell>Event</TableCell>
                            <TableCell>Path</TableCell>
                            <TableCell>Channel</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Device</TableCell>
                            <TableCell>Sub-tenant</TableCell>
                            <TableCell>Label / item</TableCell>
                            <TableCell align="right">Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((e, i) => (
                            <TableRow key={`${e.time}-${i}`} hover>
                                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                    {formatTime(e.time)}
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={e.event} variant="outlined" />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 260 }}>
                                    <Typography variant="body2" noWrap title={e.path}>
                                        {e.path}
                                    </Typography>
                                </TableCell>
                                <TableCell>{e.channel}</TableCell>
                                <TableCell>{e.country}</TableCell>
                                <TableCell>{e.device}</TableCell>
                                <TableCell>{e.sub_tenant}</TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                    <Typography variant="body2" noWrap>
                                        {[e.label, e.item_id].filter(Boolean).join(' · ')}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {e.value ? fmtNum(e.value, 2) : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </ReportStatus>
    )
}
